import { captureIframeScreenshot, captureIframeAllSections } from '../utils/screenshotCapture';
import { imageUploadService } from './imageUploadService';
import { apiService } from './api';

export interface ScreenshotsData {
  all: string[];
  selected: number[];
}

class ScreenshotService {
  private static instance: ScreenshotService;

  public static getInstance(): ScreenshotService {
    if (!ScreenshotService.instance) {
      ScreenshotService.instance = new ScreenshotService();
    }
    return ScreenshotService.instance;
  }

  /**
   * Capture screenshot from iframe and upload to Firebase
   */
  async captureAndUpload(
    iframeRef: React.RefObject<HTMLIFrameElement>,
    projectId: string
  ): Promise<string> {
    console.log('📸 Starting screenshot capture and upload...');

    try {
      // 1. Capture screenshot using the canvas utility
      const blob = await captureIframeScreenshot(iframeRef);
      console.log(`✅ Screenshot captured: ${blob.size} bytes`);

      // 2. Convert Blob to File
      const file = new File([blob], `screenshot-${Date.now()}.png`, {
        type: 'image/png',
        lastModified: Date.now()
      });

      // 3. Upload using imageUploadService
      const result = await imageUploadService.uploadImage(
        file,
        projectId,
        'screenshot'
      );

      console.log(`✅ Screenshot uploaded successfully: ${result.url}`);
      return result.url;
    } catch (error) {
      console.error('❌ Screenshot capture/upload failed:', error);
      throw error;
    }
  }

  /**
   * Capture one screenshot per game section and upload all in parallel.
   * Returns array of uploaded URLs.
   */
  async captureAndUploadAll(
    iframeRef: React.RefObject<HTMLIFrameElement>,
    projectId: string
  ): Promise<string[]> {
    console.log('📸 Capturing all sections...');
    const blobs = await captureIframeAllSections(iframeRef);
    console.log(`✅ Captured ${blobs.length} section(s)`);

    const urls = await Promise.all(blobs.map(async (blob, i) => {
      const file = new File([blob], `screenshot-${Date.now()}-${i}.png`, { type: 'image/png', lastModified: Date.now() });
      const result = await imageUploadService.uploadImage(file, projectId, 'screenshot');
      return result.url;
    }));

    console.log(`✅ Uploaded ${urls.length} screenshot(s)`);
    return urls;
  }

  /**
   * Update screenshots metadata in project data
   */
  async updateScreenshotsMetadata(
    projectId: string,
    screenshots: ScreenshotsData
  ): Promise<void> {
    try {
      console.log('💾 Updating screenshots metadata...', screenshots);

      await apiService.put('/editor/project-data/metadata', {
        projectId,
        metadata: {
          screenshots
        }
      });

      console.log('✅ Screenshots metadata updated successfully');
    } catch (error) {
      console.error('❌ Failed to update screenshots metadata:', error);
      throw error;
    }
  }

  /**
   * Delete a screenshot from Firebase and update metadata
   */
  async deleteScreenshot(
    url: string,
    projectId: string,
    currentScreenshots: ScreenshotsData
  ): Promise<ScreenshotsData> {
    try {
      console.log('🗑️ Deleting screenshot:', url);

      // 1. Delete from Firebase
      await imageUploadService.deleteImage(url);

      // 2. Find index in all array
      const index = currentScreenshots.all.indexOf(url);
      if (index === -1) {
        console.warn('⚠️ Screenshot not found in array');
        return currentScreenshots;
      }

      // 3. Remove from all array
      const newAll = currentScreenshots.all.filter((_, i) => i !== index);

      // 4. Update selected indices
      // - Remove if the deleted index was selected
      // - Adjust indices that come after the deleted one
      const newSelected = currentScreenshots.selected
        .filter(selectedIndex => selectedIndex !== index)
        .map(selectedIndex => selectedIndex > index ? selectedIndex - 1 : selectedIndex);

      const updated: ScreenshotsData = {
        all: newAll,
        selected: newSelected
      };

      // 5. Update metadata
      await this.updateScreenshotsMetadata(projectId, updated);

      console.log('✅ Screenshot deleted successfully');
      return updated;
    } catch (error) {
      console.error('❌ Failed to delete screenshot:', error);
      throw error;
    }
  }

  /**
   * Move selected screenshots to the front of the array
   */
  moveSelectedToFront(screenshots: ScreenshotsData): ScreenshotsData {
    if (screenshots.selected.length === 0) {
      return screenshots;
    }

    // 1. Extract selected screenshots by index
    const selected = screenshots.selected
      .sort((a, b) => a - b) // Sort indices to maintain order
      .map(index => screenshots.all[index]);

    // 2. Extract unselected screenshots
    const unselected = screenshots.all.filter((_, index) => 
      !screenshots.selected.includes(index)
    );

    // 3. Concatenate: [...selected, ...unselected]
    const newAll = [...selected, ...unselected];

    // 4. Update selected indices to [0, 1, 2, ...] (sequential from start)
    const newSelected = selected.map((_, index) => index);

    return {
      all: newAll,
      selected: newSelected
    };
  }
}

// Export singleton instance
export const screenshotService = ScreenshotService.getInstance();

